/**
 * PhoneVerifyBanner — banner-center source (2026-08-14 owner directive: the
 * text-message twin of EmailVerifyBanner).
 *
 * A signed-in user with a phone verification IN FLIGHT ('pending') or one
 * that ran out of time ('expired') sees a reminder in the banner rotation
 * with a one-tap resend. Dismissal is DAILY — the X hides it only until the
 * next 5:00 AM local, the same policy as the trial and email banners.
 *
 * Deliberately quiet states: no consent doc at all (the user never asked for
 * texts — the profile page is where they add a number), 'confirmed'
 * (verified — done), and 'opted_out' (carrier-level opt-out: we never nudge
 * an opted-out number; the resend path itself refuses those server-side).
 */

import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { useBannerSurface } from './bannerSurface';
import { BannerBusContext, useRegisterBanner, BannerDescriptor } from './GlobalBannerCenter';
import {
  isDismissalActive,
  readDailyDismissal,
  writeDailyDismissal,
} from './bannerDismissal';
import {
  SmsConsentState,
  requestSmsVerification,
  subscribeSmsConsent,
} from '../../services/smsVerificationClient';

const DISMISS_KEY = 'prest0n_phoneverify_dismissed';

const ResendButton = styled.button`
  flex: none;
  padding: 4px 14px;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
  color: #fff;
  background: #8a6b2a;
  border: none;
  transition: filter 0.15s ease;
  &:hover { filter: brightness(1.08); }
  &:disabled { opacity: 0.7; cursor: default; }
`;

export const PhoneVerifyBanner: React.FC = () => {
  const bus = useContext(BannerBusContext);
  const onAppSurface = useBannerSurface();

  const [uid, setUid] = useState<string | null>(null);
  const [consent, setConsent] = useState<SmsConsentState>({ status: 'none', phoneMasked: null });
  const [dismissedAt, setDismissedAt] = useState<string | null>(() => readDailyDismissal(DISMISS_KEY));
  const [resendState, setResendState] = useState<'idle' | 'sending' | 'sent' | 'failed'>('idle');

  useEffect(() => {
    const unsub = onAuthStateChanged(getAuth(), (u) => {
      setUid(u && !u.isAnonymous ? u.uid : null);
    });
    return unsub;
  }, []);

  useEffect(() => {
    if (!uid) {
      setConsent({ status: 'none', phoneMasked: null });
      return;
    }
    return subscribeSmsConsent(uid, setConsent);
  }, [uid]);

  const eligible =
    !!uid &&
    (consent.status === 'pending' || consent.status === 'expired') &&
    onAppSurface &&
    !isDismissalActive(dismissedAt);

  const handleResend = () => {
    if (resendState === 'sending' || resendState === 'sent') return;
    setResendState('sending');
    requestSmsVerification()
      .then((r) => setResendState(r.status === 'sent' ? 'sent' : 'failed'))
      .catch(() => setResendState('failed'));
  };

  const handleDismiss = () => {
    writeDailyDismissal(DISMISS_KEY);
    setDismissedAt(readDailyDismissal(DISMISS_KEY));
  };

  const copy =
    consent.status === 'expired'
      ? `your phone verification expired — resend the text to ${consent.phoneMasked ?? 'your number'} and reply YES to finish.`
      : `verify your phone — reply YES to the text we sent ${consent.phoneMasked ?? 'you'} to turn on text notifications.`;

  // The phone line (2026-09-13). "reply YES" is the whole instruction and it
  // stays; the masked number and the reason are the elaboration, one tap away.
  const shortCopy =
    consent.status === 'expired'
      ? 'phone verification expired — resend and reply YES.'
      : 'verify your phone — reply YES to our text.';

  const descriptor: BannerDescriptor | null = eligible
    ? {
        id: 'phone-verify',
        tone: 'info',
        dismissible: true,
        onDismiss: handleDismiss,
        shortContent: (
          <>
            <span>{shortCopy}</span>
            <ResendButton
              type="button"
              onClick={handleResend}
              disabled={resendState === 'sending' || resendState === 'sent'}
            >
              {resendState === 'sent' ? 'sent ✓' : resendState === 'sending' ? 'sending…' : 'resend'}
            </ResendButton>
          </>
        ),
        content: (
          <>
            <span>{copy}</span>
            <ResendButton
              type="button"
              onClick={handleResend}
              disabled={resendState === 'sending' || resendState === 'sent'}
            >
              {resendState === 'sent'
                ? 'sent ✓'
                : resendState === 'sending'
                  ? 'sending…'
                  : resendState === 'failed'
                    ? 'try again'
                    : 'resend text'}
            </ResendButton>
          </>
        ),
      }
    : null;

  useRegisterBanner(bus ? descriptor : null);

  // Banner-center-native: outside the center this source renders nothing.
  return null;
};

export default PhoneVerifyBanner;
